import { DateStr, toDateStr, toMoment } from "~/app/core/model/date-str";
import { Moment } from "moment";
import * as _ from "lodash";

interface SettingsVals {
    // Notifications
    useBouncebackNotifications: boolean;
    useReminderNotifications: boolean;
    useNewOfferNotifications: boolean;

    // Logging
    enableLogging: boolean;
    logBufferSize: number;
    logLevel: ("trace"|"info"|"warn"|"debug"|"error");

    // Environment
    env: ("prod"|"test"|"local");
    localUrl: string;
    showEnvironmentHeader: boolean;

    loadRemoteSettings: boolean;

    // Presentation
    showShareInstructions: boolean;
    swipeSensitivity: number;
    sortOffersLocal: boolean;

    // Behavior
    sponsorVideoNext: DateStr;
    sponsorVideoNextSeconds: number;
    sponsorVideoSkipDelaySeconds: number;
    signupBeforeNoOffers: boolean;
    signupBeforeAnyOffers: boolean;
    obscureUnauthenticatedOffer: boolean;
    enforceRedeemLocation: boolean;
    enforceRedeemNet: boolean;
    redeemAllowedMiles: number;
    redeemNowMiles: number;

    quickShare: boolean;
    quickScroll: boolean;
    allowReshowCashier: boolean;
    showShareCoupon: boolean;
    showBusinessCoupon: boolean;
    showMilesAwayOnCoupon: boolean;
    pollThumbsUpBeforeBounceback: boolean;
    alwaysShowBouncebacks: boolean;
    allowFiltering: boolean;
    filteringMinOffers: number;
    combinedOffersTab: boolean;
    interleavedCoupons: boolean;

    gamificationEnabled: boolean;
    gamificationDailyProgressEnabled: boolean;
    gamificationMysteryMode: boolean;
    gamificationFavoriteMode: boolean;
    favoriteCost: number;

    earnTicketsSignUpFriend: number;
    earnTicketsSignUpFromReferral: number;
    earnTicketsRedeemLocal: number;
    earnTicketsRedeemOnline: number;
    earnTicketsOpenApp: number;
    earnTicketsShare: number;
    earnTicketsOpenOffer: number;
    earnTicketsFilterOffers: number;
    earnTicketsVisitWebsite: number;
    earnTicketsFavoriteOffer: number;

    searchExpiresMinutes: number;
    searchAlertDelaySeconds: number;
    reminderNotificationEnabled: boolean;
    reminderNotificationOverride: boolean;
    reminderNotificationMinutes: number;
    shareNotificationOverride: boolean;
    shareNotificationMinutes: number;

    bounceBackNotificationOverride: boolean;
    bounceBackNotificationDelaySeconds: number;

    // Time Options
    dateOverride: boolean;
    dateSimulate: DateStr;
    hoursShift: number;

    // Testing
    testable: boolean;
    testing: boolean;

    // Sample Data
    useSampleUser: boolean;
    useSampleOffers: boolean;
}

export class Settings implements SettingsVals {
    useSampleUser: boolean = false;
    useSampleOffers: boolean = false;

    useBouncebackNotifications: boolean = true;
    useReminderNotifications: boolean = true;
    useNewOfferNotifications: boolean = true;

    sponsorVideoNext: DateStr = toDateStr(new Date());
    sponsorVideoNextSeconds: number = 60 * 5;
    sponsorVideoSkipDelaySeconds: number = 10;
    bounceBackNotificationOverride: boolean = false;
    bounceBackNotificationDelaySeconds: number = 60;

    enableLogging: boolean = false;
    logBufferSize: number = 10000;
    logLevel: ("trace"|"info"|"warn"|"debug"|"error") = "trace";

    env: ("prod"|"test"|"local") = "prod";
    // localUrl: string = "http://172.16.1.13:8080/test";
    localUrl: string = "http://127.0.0.1:8080/test";
    loadRemoteSettings: boolean = true;

    showEnvironmentHeader: boolean = true;
    showPermissionSettings: boolean = false;
    showShareInstructions: boolean = true;
    swipeSensitivity: number = 3;
    sortOffersLocal: boolean = true;
    sortOnlinePriorityShift: number = 100;

    signupBeforeNoOffers: boolean = true;
    signupBeforeAnyOffers: boolean = true;
    obscureUnauthenticatedOffer: boolean = true;
    enforceRedeemLocation: boolean = true;
    enforceRedeemNet: boolean = false;
    redeemAllowedMiles: number = 0.5;
    redeemNowMiles: number = 0.1;

    quickShare: boolean = true;
    quickScroll: boolean = true;
    allowReshowCashier: boolean = true;
    showShareCoupon: boolean = true;
    showBusinessCoupon: boolean = true;
    showMilesAwayOnCoupon: boolean = true;
    pollThumbsUpBeforeBounceback: boolean = false;
    alwaysShowBouncebacks: boolean = false;
    allowFiltering: boolean = false;
    filteringMinOffers: number = 0;
    combinedOffersTab: boolean = false;
    interleavedCoupons: boolean = true;

    gamificationEnabled: boolean = false;
    gamificationDailyProgressEnabled: boolean = false;
    gamificationMysteryMode: boolean = false;
    gamificationFavoriteMode: boolean = false;
    favoriteCost: number = 1;

    earnTicketsSignUpFriend: number = 10;
    earnTicketsSignUpFromReferral: number = 10;
    earnTicketsRedeemLocal: number = 10;
    earnTicketsRedeemOnline: number = 5;
    earnTicketsOpenApp: number = 3;
    earnTicketsShare: number = 5;
    earnTicketsOpenOffer: number = 1;
    earnTicketsFilterOffers: number = 1;
    earnTicketsVisitWebsite: number = 1;
    earnTicketsFavoriteOffer: number = 1;

    searchExpiresOverride: boolean = false;
    searchExpiresMinutes: number = 1000; // roughly one day
    searchAlertDelaySeconds: number = 30; // roughly one day
    reminderNotificationEnabled: boolean = false;
    reminderNotificationOverride: boolean = false;
    reminderNotificationMinutes: number = 5;
    shareNotificationOverride: boolean = false;
    shareNotificationMinutes: number = 5;

    dateOverride: boolean = false;
    dateSimulate: DateStr = "2019-12-01";
    hoursShift: number = 0;

    testable: boolean = false;
    testing: boolean = false;

    constructor() {
        this.preconfigureSettings("production");
    }

    deserialize(input: any) {
        _.merge(this, input);
    }

    calculateBounceBack(redeemedDate: Date, sample: boolean): Date {
        let when: Date = null;
        if (redeemedDate) {
            if (this.bounceBackNotificationOverride) {
                // override for testing to perform the notification on a timer delay rather than next day
                when = new Date(redeemedDate.getTime() + this.bounceBackNotificationDelaySeconds * 1000);
            } else if (sample) {
                when = new Date(redeemedDate.getTime() + 15 * 1000);
            } else {
                // 10am the next day
                let moment: Moment = toMoment(toDateStr(redeemedDate));
                moment = moment.add(1, "days");
                moment = moment.set("hour", 10);
                moment = moment.set("minute", 0);
                moment = moment.set("second", 0);
                moment = moment.set("millisecond", 0);
                when = moment.toDate();
            }
        }
        return when;
    }

    setTimeOfDay(date: Date|DateStr, hour: number, minute: number = 0, second: number = 0): Date {
        let when: Date = null;
        if (date) {
            // 10am the next day
            let moment: Moment = toMoment(date);
            moment = moment.set("hour", hour);
            moment = moment.set("minute", minute);
            moment = moment.set("second", second);
            moment = moment.set("millisecond", 0);
            when = moment.toDate();
        }
        return when;
    }

    calculateNextVideoTime(lastVideo: Date): Date {
        let when: Date = null;
        if (!lastVideo) {
            when = this.calculateCurrentTime();
        } else {
            when = new Date(lastVideo.getTime() + this.sponsorVideoNextSeconds * 1000);
        }
        return when;
    }

    calculateSearchExpires(lastSearch: Date): Date {
        let when: Date = null;
        if (!lastSearch) {
            when = this.calculateCurrentTime();
        } else {
            if (this.searchExpiresOverride) {
                // override for testing to search refresh
                when = new Date(lastSearch.getTime() + this.searchExpiresMinutes * 60000);
            } else {
                // 5am the next day
                let moment: Moment = toMoment(lastSearch);
                moment = moment.add(1, "days");
                moment = moment.set("hour", 5);
                moment = moment.set("minute", 0);
                moment = moment.set("second", 0);
                moment = moment.set("millisecond", 0);
                when = moment.toDate();
            }
        }
        return when;
    }

    calculateNotificationReminder(lastSearch: Date): Date {
        let when: Date = null;
        if (!lastSearch) {
            when = this.calculateCurrentTime();
        } else {
            if (this.reminderNotificationOverride) {
                // override for testing to perform the notification on a timer delay rather than next day
                when = new Date(lastSearch.getTime() + this.reminderNotificationMinutes * 60000);
            } else {
                // 10am the next day
                let moment: Moment = toMoment(new Date());
                moment = moment.day("Monday");
                moment = moment.set("hour", 10);
                moment = moment.set("minute", 0);
                moment = moment.set("second", 0);
                moment = moment.set("millisecond", 0);
                when = moment.add(14, "days").toDate();
            }
        }
        return when;
    }

    calculateDaysOutReminder(lastSearch: Date, daysOut: number): Date {
        let when: Date = null;
        if (!lastSearch) {
            when = this.calculateCurrentTime();
        } else {
            if (this.reminderNotificationOverride) {
                // override for testing to perform the notification on a timer delay rather than next day
                when = new Date(lastSearch.getTime() + this.reminderNotificationMinutes * 60000);
            } else {
                // 10am the next day
                let moment: Moment = toMoment(new Date());
                moment = moment.set("hour", 10);
                moment = moment.set("minute", 0);
                moment = moment.set("second", 0);
                moment = moment.set("millisecond", 0);
                when = moment.add(daysOut, "days").toDate();
            }
        }
        return when;
    }

    calculateShareReminder(): Date {
        let when: Date = this.calculateCurrentTime();
        if (this.shareNotificationOverride) {
            // override for testing to perform the notification on a timer delay rather than next day
            when = new Date(new Date().getTime() + this.shareNotificationMinutes * 60000);
        } else {
            // 10am the next day
            let moment: Moment = toMoment(new Date());
            moment = moment.set("hour", 10);
            moment = moment.set("minute", 0);
            moment = moment.set("second", 0);
            moment = moment.set("millisecond", 0);
            when = moment.add(1, "days").toDate();
        }
        return when;
    }

    calculateCurrentTime(): Date {
        let when: Date = new Date();
        if (this.dateOverride) {
            try {
                const parsedMoment = toMoment(toDateStr(this.dateSimulate));
                if (parsedMoment.isValid()) {
                    // 10am the next day
                    const moment: Moment = toMoment(toDateStr(when));

                    moment.set("year", parsedMoment.get("year"));
                    moment.set("month", parsedMoment.get("month"));
                    moment.set("date", parsedMoment.get("date"));

                    when = moment.toDate();
                    when.setTime(when.getTime() + (this.hoursShift * 60 * 60 * 1000));
                }
            } catch (error) {
                // just ignore
            }
        }
        return when;
    }

    preconfigureSettings(mode: "production" | "beta" | "testmode") {
        if (mode === "production") {
            this.useSampleUser = false;
            this.useSampleOffers = false;

            this.useBouncebackNotifications = true;
            this.useReminderNotifications = true;
            this.useNewOfferNotifications = true;

            this.sponsorVideoNextSeconds = 60 * 5;
            this.sponsorVideoSkipDelaySeconds = 10;
            this.bounceBackNotificationOverride = false;

            this.enableLogging = false;
            this.logLevel = "error";

            this.env = "prod";
            this.loadRemoteSettings = true;
            this.showEnvironmentHeader = true;

            this.showPermissionSettings = false;
            this.showShareInstructions = true;
            this.swipeSensitivity = 3;
            this.sortOffersLocal = true;
            this.sortOnlinePriorityShift = 100;

            this.signupBeforeNoOffers = true;
            this.signupBeforeAnyOffers = true;
            this.obscureUnauthenticatedOffer = true;
            this.enforceRedeemLocation = true;
            this.enforceRedeemNet = false;
            this.redeemAllowedMiles = 0.5;
            this.redeemNowMiles = 0.1;
            this.quickShare = true;
            this.quickScroll = false;
            this.allowReshowCashier = true;
            this.showShareCoupon = true;
            this.showBusinessCoupon = true;
            this.showMilesAwayOnCoupon = true;
            this.alwaysShowBouncebacks = false;
            this.allowFiltering = false;
            this.filteringMinOffers = 0;
            this.combinedOffersTab = false;

            this.gamificationEnabled = false;
            this.gamificationDailyProgressEnabled = false;

            this.searchExpiresOverride = false;
            this.reminderNotificationEnabled = false;
            this.reminderNotificationOverride = false;
            this.shareNotificationOverride = false;

            this.dateOverride = false;

            this.testable = false;
            this.testing = false;
        } else if (mode === "beta") {
            this.useSampleUser = false;
            this.useSampleOffers = false;

            this.useBouncebackNotifications = true;
            this.useReminderNotifications = true;
            this.useNewOfferNotifications = true;

            this.sponsorVideoNextSeconds = 60 * 5;
            this.sponsorVideoSkipDelaySeconds = 10;
            this.bounceBackNotificationOverride = true;
            this.bounceBackNotificationDelaySeconds = 300;

            this.enableLogging = false;
            this.logBufferSize = 10000;
            this.logLevel = "trace";

            this.env = "test";
            this.showEnvironmentHeader = true;
            this.loadRemoteSettings = true;

            this.signupBeforeNoOffers = true;
            this.obscureUnauthenticatedOffer = true;
            this.enforceRedeemLocation = false;
            this.enforceRedeemNet = false;
            this.allowFiltering = true;
            this.filteringMinOffers = 0;
            this.combinedOffersTab = false;

            this.gamificationEnabled = false;
            this.gamificationDailyProgressEnabled = false;

            this.searchExpiresOverride = true;
            this.searchExpiresMinutes = 720;
            this.searchAlertDelaySeconds = 30;
            this.reminderNotificationEnabled = false;
            this.reminderNotificationOverride = true;
            this.reminderNotificationMinutes = 720;
            this.shareNotificationOverride = true;
            this.shareNotificationMinutes = 60;

            this.dateOverride = false;
        } else if (mode === "testmode") {
            this.testing = true;

            this.enableLogging = false;
            this.logBufferSize = 10000;
            this.logLevel = "trace";

            this.sponsorVideoNextSeconds = 60 * 60;
            this.sponsorVideoSkipDelaySeconds = 2;
            this.bounceBackNotificationOverride = true;
            this.bounceBackNotificationDelaySeconds = 10;

            this.env = "test";
            this.showEnvironmentHeader = true;
            this.loadRemoteSettings = false;
            this.allowFiltering = true;
            this.filteringMinOffers = 0;

            this.gamificationEnabled = false;
            this.gamificationDailyProgressEnabled = false;

            this.enforceRedeemLocation = false;
            this.enforceRedeemNet = false;
            this.redeemAllowedMiles = 0.5;
            this.redeemNowMiles = 0.1;
        }
    }
}
